import React, { useCallback, useState } from 'react';
import Field from '../../../../../components/Field';
import TextInput from '../../../../../components/TextInput';
import SegmentedControl from '../../../../../components/SegmentedControl';
import { BALANCE_LEAVE_LABELS, BalanceLeaveType, LeaveAdjustmentInput } from '../../../config';
import { parseNumber } from './leaveFormat';

interface LeaveAdjustmentFormProps {
  leaveType: BalanceLeaveType;
  /**
   * Today as the database has it (the office's date), for the default. The
   * browser's own date can be a day ahead of it, and the database refuses an
   * adjustment dated in the future.
   */
  today: string;
  onSubmit: (input: LeaveAdjustmentInput) => Promise<string | null>;
  onCancel: () => void;
}

type Direction = 'add' | 'remove';

interface AdjustmentErrors {
  hours?: string;
  date?: string;
  note?: string;
  general?: string;
}

/**
 * A manual correction to a balance. Hours are typed as a plain amount and the
 * direction picked beside them, so nobody has to remember that taking leave
 * away means typing a minus sign.
 */
const LeaveAdjustmentForm: React.FC<LeaveAdjustmentFormProps> = ({ leaveType, today, onSubmit, onCancel }) => {
  const [direction, setDirection] = useState<Direction>('add');
  const [hours, setHours] = useState<string>('');
  const [date, setDate] = useState<string>(today);
  const [note, setNote] = useState<string>('');
  const [errors, setErrors] = useState<AdjustmentErrors>({});
  const [saving, setSaving] = useState<boolean>(false);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const amount = parseNumber(hours);
    const next: AdjustmentErrors = {};
    if (amount === null || amount <= 0) next.hours = 'Enter the hours, above zero';
    if (!date) next.date = 'Enter the date it applies from';
    if (!note.trim()) next.note = 'Say what the adjustment is for';
    if (Object.keys(next).length > 0 || amount === null) {
      setErrors(next);
      return;
    }

    setSaving(true);
    const failure = await onSubmit({
      leave_type: leaveType,
      hours: direction === 'add' ? amount : -amount,
      effective_date: date,
      note: note.trim()
    });
    setSaving(false);

    if (failure) {
      setErrors({ general: failure });
      return;
    }
    onCancel();
  }, [hours, date, note, direction, leaveType, onSubmit, onCancel]);

  return (
    <form className="disclosure-card employee-tab__disclosure" onSubmit={handleSubmit}>
      {errors.general && (
        <p className="field__error" role="alert">{errors.general}</p>
      )}
      <div className="pdm-grid pdm-grid--3">
        <Field label={`Adjust ${BALANCE_LEAVE_LABELS[leaveType].toLowerCase()}`}>
          <SegmentedControl<Direction>
            options={[
              { value: 'add', label: 'Add' },
              { value: 'remove', label: 'Take away' }
            ]}
            value={direction}
            onChange={(v) => v && setDirection(v)}
            fullWidth
            disabled={saving}
            aria-label="Add to or take away from the balance"
          />
        </Field>
        <Field label="Hours" htmlFor="leave-adj-hours" required error={errors.hours}>
          <TextInput
            id="leave-adj-hours"
            type="number"
            step="0.25"
            min="0"
            value={hours}
            invalid={!!errors.hours}
            disabled={saving}
            onChange={(e) => setHours(e.target.value)}
          />
        </Field>
        <Field label="Applies from" htmlFor="leave-adj-date" required error={errors.date}>
          <TextInput
            id="leave-adj-date"
            type="date"
            max={today}
            value={date}
            invalid={!!errors.date}
            disabled={saving}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        <Field
          label="Reason"
          htmlFor="leave-adj-note"
          className="span-all"
          required
          error={errors.note}
          hint="Shown beside the adjustment, so the balance can be checked later."
        >
          <TextInput
            id="leave-adj-note"
            value={note}
            invalid={!!errors.note}
            disabled={saving}
            placeholder="e.g. Leave cashed out, correction to match payroll"
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
      </div>
      <div className="disclosure-card__actions">
        <button type="button" className="pill-btn pill-btn--ghost pill-btn--sm" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="pill-btn pill-btn--primary pill-btn--sm" disabled={saving}>
          {saving ? 'Saving…' : 'Save adjustment'}
        </button>
      </div>
    </form>
  );
};

export default LeaveAdjustmentForm;
